import { PRODUCT_CATEGORY_CONFIG } from "./category-config";
import {
  isCategoryFieldConfig,
  subcategoryPathFor,
  type CategoryFieldConfig,
} from "./subcategory-slugs";
import { getMetadataKeyFromUrlSlug } from "@/lib/products/catalog/resolution";

const FIELD_LABELS: Record<string, string> = {
  condition: "Condition",
  type: "Type",
  size: "Size",
  entry: "Entry",
  material: "Material",
  bottoms: "Bottom",
  equipmenttype: "Equipment type",
  powersource: "Power source",
  boxsize: "Box size",
  boxtype: "Box type",
  top: "Top",
};

export interface CategoryFacetOption {
  value: string;
  label: string;
  href: string;
  count: number;
  truncated: boolean;
}

export interface CategoryFacetGroup {
  field: string;
  label: string;
  options: CategoryFacetOption[];
  listed: CategoryFacetOption[];
  truncated: boolean;
}

export interface CategoryFacetRequest {
  field: string;
  values: string[];
}

function hasCategoryConfig(
  key: string,
): key is keyof typeof PRODUCT_CATEGORY_CONFIG {
  return key in PRODUCT_CATEGORY_CONFIG;
}

function configuredFields(productSlug: string): CategoryFieldConfig[] {
  const metadataKey = getMetadataKeyFromUrlSlug(productSlug);
  if (!metadataKey || !hasCategoryConfig(metadataKey)) return [];
  return Object.values(PRODUCT_CATEGORY_CONFIG[metadataKey]).filter(
    isCategoryFieldConfig,
  );
}

export function formatFacetCount(count: number, truncated: boolean): string {
  return truncated ? `${count}+` : String(count);
}

export function shortFacetLabel(label: string, productName: string): string {
  const suffix = ` ${productName}`.toLowerCase();
  return label.toLowerCase().endsWith(suffix)
    ? label.slice(0, label.length - suffix.length)
    : label;
}

export function facetQueryFor(fieldName: string, fieldValue: string): string {
  if (fieldName === "condition") return `grade=${encodeURIComponent(fieldValue.toLowerCase())}`;
  const walls = /^(\d)-wall$/i.exec(fieldValue);
  if (walls) return `walls=${walls[1]}`;
  const inches = /^(\d+)\s*[x×]\s*(\d+)(?:\s*(?:in|"))?$/i.exec(fieldValue.trim());
  if (inches) return `fp=${inches[1]}x${inches[2]}`;
  return `spec.${encodeURIComponent(fieldName)}=${encodeURIComponent(fieldValue.trim())}`;
}

export function quoteHrefFor(productSlug: string, facetQuery = ""): string {
  const base = `/request-quote?type=buy&product=${encodeURIComponent(productSlug)}`;
  return facetQuery ? `${base}&${facetQuery}` : base;
}

export function hasCategoryFacets(productSlug: string): boolean {
  return configuredFields(productSlug).length > 0;
}

export function buildFacetRequests(productSlug: string): CategoryFacetRequest[] {
  return configuredFields(productSlug).map((fieldConfig) => ({
    field: fieldConfig.field,
    values: fieldConfig.categories.map((category) => category.value),
  }));
}

export function buildFacetGroups(
  productSlug: string,
  counts: ReadonlyArray<{ field: string; value: string; count: number }>,
  truncated = false,
): CategoryFacetGroup[] {
  const countByKey = new Map(
    counts.map((row) => [`${row.field}|${row.value}`, row.count]),
  );
  return configuredFields(productSlug).map((fieldConfig) => {
    const options = fieldConfig.categories
      .map((category) => ({
        value: category.value,
        label: category.label,
        href: subcategoryPathFor(productSlug, fieldConfig.field, category.value),
        count: countByKey.get(`${fieldConfig.field}|${category.value}`) ?? 0,
        truncated,
      }))
      .sort((left, right) => right.count - left.count);
    return {
      field: fieldConfig.field,
      label: FIELD_LABELS[fieldConfig.field] ?? fieldConfig.field,
      options,
      listed: truncated ? options : options.filter((option) => option.count > 0),
      truncated,
    };
  });
}
